// Modal grid of preset thumbnails. Renders each preset through a
// short-lived offline pipeline at small size (no caching across opens —
// these are one-shot previews).

import { parseDsl } from '@/core/dsl';
import { migrate } from '@/core/migrate';
import { createPipeline } from '@/core/pipeline';
import type { AppState } from '@/core/types';
import { INITIAL_STATE } from '@/core/types';
import { el, on } from '@/lib/dom';
import { PRESETS, type Preset } from '@/presets/presets';
import { loadPreset } from '@/presets/load';

const THUMB_SIZE = 96;

export function openPresetGallery(): void {
  const dim = el('div', { class: 'modal-backdrop' });
  const dialog = el('div', { class: 'modal modal-presets', role: 'dialog', ariaLabel: 'Presets' });

  const head = el('div', { class: 'modal-head' }, [
    el('div', { class: 'modal-title' }, ['Presets']),
    el('div', { class: 'modal-subtitle' }, ['Pick a starting point. Loading replaces your stack — undo brings it back.']),
  ]);

  const grid = el('div', { class: 'preset-grid' });

  for (const preset of PRESETS) {
    grid.appendChild(buildCard(preset, () => {
      if (loadPreset(preset)) close();
    }));
  }

  dialog.append(head, grid);
  dim.appendChild(dialog);
  document.body.appendChild(dim);

  function close(): void {
    dim.remove();
    offKey();
    offClick();
  }

  const offKey = on(window, 'keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });
  const offClick = on(dim, 'click', (ev) => {
    if (ev.target === dim) close();
  });
}

function buildCard(preset: Preset, onPick: () => void): HTMLElement {
  const canvas = el('canvas', { class: 'preset-thumb' }) as HTMLCanvasElement;
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;

  const card = el('button', { class: 'preset-card', ariaLabel: `Load preset ${preset.name}` }, [
    canvas,
    el('div', { class: 'preset-meta' }, [
      el('div', { class: 'preset-name' }, [preset.name]),
      el('div', { class: 'preset-tags mono' }, [preset.tags.join(' · ')]),
    ]),
  ]);
  on(card, 'click', onPick);

  // Render thumbnail asynchronously
  void renderThumb(preset, canvas);

  return card;
}

async function renderThumb(preset: Preset, canvas: HTMLCanvasElement): Promise<void> {
  const parsed = parseDsl(preset.dsl);
  if (!parsed.ok) return;
  const migrated = migrate(parsed.value);
  if (!migrated) return;
  const state: AppState = {
    ...INITIAL_STATE,
    resolution: { w: THUMB_SIZE, h: THUMB_SIZE },
    layers: migrated.layers,
  };
  const pipeline = createPipeline();
  const result = await pipeline.render(state);
  if (!result.image) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.putImageData(result.image, 0, 0);
}
